import React, { useEffect } from "react";
import hljs from "highlight.js";
import ContentHeader from "./ContentHeader";
import ContentAds from "./ContentAds";
import ContentTableOfContents, { IContentTableOfContentsLink } from "./ContentTableOfContents";
import "./ArticleBody.scss";
import { slugify } from "../../includes/utils";

export interface IArticleBodyProps {
  title: string;
  labels: string[];
  createdDate: Date;
  readTime: number;
  anchors: IContentTableOfContentsLink[];
  html: string;
}

const ArticleBody = ({ title, labels, createdDate, readTime, anchors, html }: IArticleBodyProps): JSX.Element => {
  const content: JSX.Element = <section className="ArticleBody__content" dangerouslySetInnerHTML={{ __html: html }}/>;

  useEffect(() => {
    // Syntax highlight code blocks
    for (const codeBlock of document.querySelectorAll(".ArticleBody__content code *")) {
      hljs.highlightElement(codeBlock as HTMLElement);
    }

    // Add copy functionality to section links
    const copyToClipboard = (button: HTMLElement, url: string) => {
      navigator.clipboard.writeText(url)
        .then(() => {
          button.classList.add("active");
          window.setTimeout((): void => {
            button.classList.remove("active");
            button.blur();
          }, 1000);
        }).catch(() => {
          alert("Could not copy link to clipboard! Please check your browser settings.");
        });
    };

    const listeners: [Element, () => void][] = [];
    for (const button of document.querySelectorAll("h2 button, h3 button")) {
      const slug: string = slugify(button?.parentElement?.textContent ?? "");
      if (slug === "") {
        continue;
      }

      const url: string = `${window.location.origin}${window.location.pathname}#${slug}${window.location.search}`;
      const onClick = (): void => copyToClipboard(button as HTMLElement, url);

      listeners.push([button, onClick]);
      button.addEventListener("click", onClick);
    }

    return (): void => {
      for (const listener of listeners) {
        listener[0].removeEventListener("click", listener[1]);
      }

      for (const button of document.querySelectorAll("h2 button.active, h3 button.active")) {
        button.classList.remove("active");
      }
    };
  }, []);

  return (
    <div className="ArticleBody__wrapper">
      <ContentHeader title={title} labels={labels} createdDate={createdDate} readTime={readTime}/>
      <div className="ArticleBody">
        <ContentTableOfContents title={title} anchors={anchors}/>
        {content}
        <ContentAds/>
      </div>
    </div>
  );
};
export default ArticleBody;